"use client";

import { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";

const COLORS = ["#7cb5ec", "#434348"];

function toCurrency(num: number) {
    return "$" + num.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
}

interface MortgagePieChartProps {
    principalAndInterest: number;
    taxesAndInsurance: number;
}

const MortgagePieChart = ({ principalAndInterest, taxesAndInsurance }: MortgagePieChartProps) => {
    const total = principalAndInterest + taxesAndInsurance;

    const data = [
        { name: "Principal & Interest", value: principalAndInterest },
        { name: "Taxes & Insurance", value: taxesAndInsurance },
    ];

    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    return (
        <div className="mt-8">
            <h3 className="text-xl font-bold text-center text-[#212459] mb-4">Payment Breakdown</h3>
            <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        outerRadius={150}
                        activeIndex={activeIndex ?? undefined}
                        activeShape={{
                            fill: COLORS[activeIndex ?? 0],
                            stroke: "#ffffff",
                            strokeWidth: 3,
                        }}
                        labelLine={false}
                        label={({ value }) => {
                            const percent = ((value / total) * 100).toFixed(0);
                            return `${toCurrency(value)} (${percent}%)`;
                        }}
                        dataKey="value"
                        isAnimationActive={true}
                        animationDuration={1200}
                        paddingAngle={6}
                        onMouseEnter={(_, index) => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(null)}
                    >
                        {data.map((entry, index) => (
                            <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(value: number) => `${toCurrency(value)} (${((value / total) * 100).toFixed(0)}%)`}
                    />
                    <Legend
                        formatter={(value: string) => {
                            const item = data.find((d) => d.name === value);
                            if (!item) return value;
                            const percent = ((item.value / total) * 100).toFixed(0);
                            return `${value} - ${toCurrency(item.value)} (${percent}%)`;
                        }}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MortgagePieChart;
